import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Loader2, RefreshCw, ExternalLink, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { formatIDR } from "@/lib/constants";
import OverReceivedBadge from "@/components/OverReceivedBadge";

// 16-OVERRECEIVED-R2 (2026-06-14, user request): Tab "Verifikasi Penerimaan" di
// Laporan Keuangan — daftar SEMUA proyek ter-flag penerimaan di atas 100% +
// status klasifikasinya. Finance/Owner dapat mengklasifikasi langsung dari sini
// (badge yang sama dengan halaman proyek). READ-ONLY terhadap angka transaksi.
export default function VerifikasiPenerimaanTab() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fStatus, setFStatus] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.get("/overreceived/flagged")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Gagal memuat data verifikasi penerimaan."))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const summary = data?.summary || {};
  const categories = data?.categories || [];

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((it) => {
      if (fStatus === "unclassified" && it.classification) return false;
      if (fStatus && fStatus !== "unclassified" && it.classification?.category !== fStatus) return false;
      if (needle && !(`${it.project_name} ${it.client || ""}`.toLowerCase().includes(needle))) return false;
      return true;
    });
  }, [items, fStatus, q]);

  const onClassified = useCallback((pid, doc) => {
    setData((prev) => prev ? ({
      ...prev,
      items: prev.items.map((it) => it.project_id === pid ? { ...it, classification: doc } : it),
      summary: {
        ...prev.summary,
        classified: prev.items.filter((it) => (it.project_id === pid ? doc : it.classification)).length,
        unclassified: prev.items.filter((it) => !(it.project_id === pid ? doc : it.classification)).length,
      },
    }) : prev);
  }, []);

  return (
    <div className="bg-white border border-slate-200" data-testid="verifikasi-penerimaan-tab">
      <div className="px-4 py-2.5 border-b border-slate-200 flex items-center gap-2 flex-wrap">
        <ShieldCheck size={14} className="text-[#0B1D3A]" />
        <h3 className="text-sm font-semibold text-[#0B1D3A]">Verifikasi Penerimaan di Atas 100%</h3>
        <span className="text-[10px] text-slate-400">
          Proyek dengan penerimaan melebihi tagihan kas (&gt; Rp1). Klasifikasi tidak mengubah angka transaksi.
        </span>
        <button type="button" onClick={load} data-testid="vp-refresh"
          className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50">
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Muat Ulang
        </button>
      </div>

      {/* Kartu ringkasan */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 px-4 py-3 border-b border-slate-100" data-testid="vp-summary">
        <MiniCard label="Proyek Ter-flag" value={summary.total_flagged ?? "—"} testid="vp-card-flagged" />
        <MiniCard label="Belum Diklasifikasi" value={summary.unclassified ?? "—"} tone="rose" testid="vp-card-unclassified" />
        <MiniCard label="Sudah Diklasifikasi" value={summary.classified ?? "—"} tone="emerald" testid="vp-card-classified" />
        <MiniCard label="Total Kelebihan" value={summary.total_excess != null ? formatIDR(summary.total_excess) : "—"} tone="amber" testid="vp-card-excess" />
      </div>

      {/* Filter */}
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2 flex-wrap" data-testid="vp-filter-bar">
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="vp-filter-status"
          className="border border-slate-300 py-1.5 px-2 text-xs bg-white">
          <option value="">Semua Status Klasifikasi</option>
          <option value="unclassified">Belum Diklasifikasi</option>
          {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <div className="relative">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari proyek / client…"
            data-testid="vp-search" className="border border-slate-300 py-1.5 pl-7 pr-2 text-xs bg-white w-56" />
        </div>
        <span className="text-[10px] text-slate-400 tabular-nums" data-testid="vp-count">{filtered.length} dari {items.length} proyek</span>
      </div>

      {error ? (
        <div className="p-6 text-center" data-testid="vp-error">
          <AlertTriangle size={22} className="mx-auto text-rose-400 mb-2" />
          <div className="text-xs text-rose-600 mb-3">{error}</div>
          <button type="button" onClick={load} data-testid="vp-retry"
            className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold bg-[#0B1D3A] text-white">Coba Lagi</button>
        </div>
      ) : loading && items.length === 0 ? (
        <div className="p-6 text-center text-[11px] text-slate-400" data-testid="vp-loading">
          <Loader2 size={14} className="inline animate-spin mr-1.5" />Memuat daftar verifikasi…
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-6 text-center text-[11px] text-slate-400" data-testid="vp-empty">
          {items.length === 0 ? "Tidak ada proyek dengan penerimaan di atas 100%. Semua penerimaan wajar." : "Tidak ada proyek yang cocok dengan filter."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[980px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                {["Proyek", "Client", "Tagihan Kas", "Diterima", "Kelebihan", "Pelunasan", "Status Klasifikasi"].map((h) => (
                  <th key={h} className="px-3 py-2 overline text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="vp-tbody" className={loading ? "opacity-50" : ""}>
              {filtered.map((it) => (
                <tr key={it.project_id} data-testid={`vp-row-${it.project_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <button type="button" data-testid={`vp-open-${it.project_id}`}
                      onClick={() => nav(`/projects/${it.project_id}/monitoring`)}
                      className="inline-flex items-center gap-1 font-semibold text-[#0B1D3A] hover:text-[#FF5A1F] text-left">
                      <ExternalLink size={11} className="shrink-0" /> {it.project_name}
                    </button>
                    <div className="text-[9px] text-slate-400 mt-0.5 uppercase tracking-wider">
                      {({ new_project: "Proyek Baru", variation_order: "VO", working_order: "WO" })[it.project_type] || it.project_type}
                      {it.status === "completed" ? " · Selesai" : ""}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{it.client || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">{formatIDR(it.cash_due)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{formatIDR(it.actual_received)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-rose-700" data-testid={`vp-excess-${it.project_id}`}>
                    {formatIDR(it.excess_amount)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-700 font-semibold">
                    {it.settlement_pct != null ? `${it.settlement_pct.toLocaleString("id-ID")}%` : "—"}
                  </td>
                  <td className="px-3 py-2" data-testid={`vp-status-${it.project_id}`}>
                    <OverReceivedBadge
                      projectId={it.project_id}
                      overReceived
                      excessAmount={it.excess_amount}
                      classification={it.classification}
                      onClassified={onClassified}
                    />
                    {it.classification?.classified_by_name && (
                      <div className="text-[9px] text-slate-400 mt-0.5">oleh {it.classification.classified_by_name}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MiniCard({ label, value, tone = "slate", testid }) {
  const toneCls = { slate: "text-[#0B1D3A]", rose: "text-rose-700", emerald: "text-emerald-700", amber: "text-amber-700" }[tone];
  return (
    <div className="border border-slate-200 px-3 py-2" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-sm font-bold tabular-nums mt-0.5 ${toneCls}`}>{value}</div>
    </div>
  );
}
